import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";

import { SurfaceService } from "../../surface/surface.service";
import { LoginService } from "../authentication.service";
import { bounceToLogin } from "./return-url";
import { isTokenExpired, readStoredToken } from "./session-token";

/**
 * Admits an account to `/manage`.
 *
 * Entry is decided by {@link SurfaceService}, not by a stored "current role": an account with a
 * staff membership belongs on this surface even when it is also enrolled as a learner. That is
 * what lets both surfaces be live at once, and it is why the old `switchRole()` — which mutated
 * `currentUser.role` purely to get past this check — is gone.
 */
@Injectable()
export class AuthGuard {
  private readonly router = inject(Router);
  private readonly loginService = inject(LoginService);
  private readonly surfaces = inject(SurfaceService);

  canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean {
    const token = readStoredToken();
    if (token === null) {
      this.loginService.changeFlag(false);
      bounceToLogin(this.router, state?.url);
      return false;
    }

    if (isTokenExpired(token)) {
      this.loginService.removeLocalStorage();
      bounceToLogin(this.router, state?.url);
      return false;
    }

    if (!this.surfaces.isAvailable("manage")) {
      this.loginService.changeFlag(false);
      void this.router.navigate([this.surfaces.isAvailable("learn") ? "/learn/dashboard" : "/login"]);
      return false;
    }

    this.loginService.changeFlag(true);
    return true;
  }
}
